import { Inline, Stack } from '@etg/wings';
import { Check } from '@phosphor-icons/react';
import { useTranslation } from '@eti/providers';
import { EventSegmentType } from '@eti/schema-types';

const STOPOVER_TYPE_TO_CONTENT: Record<
  | typeof EventSegmentType.ShortStopover
  | typeof EventSegmentType.OneDayStopover
  | typeof EventSegmentType.OneNightStopover
  | typeof EventSegmentType.TwoNightsStopover,
  {
    dataTestIdPrefix: string;
    descriptionKey: string;
  }
> = {
  [EventSegmentType.ShortStopover]: {
    dataTestIdPrefix: 'short-stopover',
    descriptionKey: 'TripDetails.Details.StopoverInRome.ShortStopover.Description',
  },
  [EventSegmentType.OneDayStopover]: {
    dataTestIdPrefix: 'one-day-stopover',
    descriptionKey: 'TripDetails.Details.StopoverInRome.OneDayStopover.Description',
  },
  [EventSegmentType.OneNightStopover]: {
    dataTestIdPrefix: 'one-night-stopover',
    descriptionKey: 'TripDetails.Details.StopoverInRome.OneNightStopover.Description',
  },
  [EventSegmentType.TwoNightsStopover]: {
    dataTestIdPrefix: 'two-nights-stopover',
    descriptionKey: 'TripDetails.Details.StopoverInRome.TwoNightsStopover.Description',
  },
};

interface StopoverInRomeContentProps {
  type:
    | typeof EventSegmentType.ShortStopover
    | typeof EventSegmentType.OneDayStopover
    | typeof EventSegmentType.OneNightStopover
    | typeof EventSegmentType.TwoNightsStopover;
}

const StopoverInRomeContent = ({ type }: StopoverInRomeContentProps) => {
  const { t } = useTranslation();
  const isShortStopover = EventSegmentType.ShortStopover === type;
  const stopoverContent = STOPOVER_TYPE_TO_CONTENT[type];
  const hasFastTrackTextKey = Boolean(t('TripDetails.Details.StopoverInRome.FastTrack'));
  const hasAutoCheckinTextKey = Boolean(t('TripDetails.Details.StopoverInRome.AutoCheckin'));
  const hasExclusiveDealsTextKey = Boolean(t('TripDetails.Details.StopoverInRome.ExclusiveDeals'));

  return (
    <Stack spacing={8}>
      <div>{t(stopoverContent.descriptionKey)}</div>
      {hasFastTrackTextKey && (
        <Inline noWrap spacing={4}>
          <Check
            color="#0057a7"
            data-testid={`${stopoverContent.dataTestIdPrefix}-fast-track-check-icon`}
            size={18}
            weight="bold"
          />
          <div>{t('TripDetails.Details.StopoverInRome.FastTrack')}</div>
        </Inline>
      )}
      {hasAutoCheckinTextKey && (
        <Inline noWrap spacing={4}>
          <Check
            color="#0057a7"
            data-testid={`${stopoverContent.dataTestIdPrefix}-auto-checkin-icon`}
            size={18}
            weight="bold"
          />
          <div>{t('TripDetails.Details.StopoverInRome.AutoCheckin')}</div>
        </Inline>
      )}
      {!isShortStopover && hasExclusiveDealsTextKey && (
        <Inline noWrap spacing={4}>
          <Check
            color="#0057a7"
            data-testid={`${stopoverContent.dataTestIdPrefix}-exclusive-deals-icon`}
            size={18}
            weight="bold"
          />
          <div>{t('TripDetails.Details.StopoverInRome.ExclusiveDeals')}</div>
        </Inline>
      )}
    </Stack>
  );
};

export default StopoverInRomeContent;
